import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { confirmTossPayment } from '../../services/tossPaymentService';
import { purchaseDB } from '../../services/partnerDashboardService';
import {
  doc, updateDoc, addDoc, getDoc, getDocs, query, where, collection,
  serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase/config';

// 결제 이력 + 미수금/수수료 동기화 (payment_request 분기 전용)
const finalizePaymentRequestPayment = async ({
  paymentRequestId, orderId, paymentKey, amount, depositorName, depositorPhone,
}) => {
  // 1) payment_request 업데이트
  await updateDoc(doc(db, 'partner_payment_requests', paymentRequestId), {
    status: 'completed',
    paid_at: serverTimestamp(),
    paid_amount: amount,
    payment_order_id: orderId,
    payment_key: paymentKey || null,
    depositor_name: depositorName || null,
    depositor_phone: depositorPhone || null,
    updatedAt: serverTimestamp(),
  });

  // 2) payment_request 데이터 조회 (이력 기록 + 동기화 매칭에 사용)
  const reqSnap = await getDoc(doc(db, 'partner_payment_requests', paymentRequestId));
  const reqData = reqSnap.exists() ? reqSnap.data() : {};
  const partnerId = reqData?.partner_id || null;
  const serviceRequestId = reqData?.service_request_id || null;

  // 3) payments 결제 이력 기록
  await addDoc(collection(db, 'payments'), {
    kind: 'payment_request',
    payment_order_id: orderId,
    payment_key: paymentKey || null,
    amount,
    partner_id: partnerId,
    payment_request_id: paymentRequestId,
    service_request_id: serviceRequestId,
    label: reqData?.label || '본사 청구 결제',
    method: reqData?.method || null,
    status: 'completed',
    paid_at: serverTimestamp(),
    created_at: serverTimestamp(),
  });

  // 4) 연결된 receivables → paid, commissions → paid_by_partner: true
  // 매칭 우선순위:
  //  (a) payment_request.receivable_ids (배열) — 명시적 1:N 매핑
  //  (b) payment_request.receivable_id (단일)
  //  (c) service_request_id 기준 receivables/commissions
  //  (d) 위 모두 없으면 payment_request_id로 직접 매칭된 receivables 검색
  const batch = writeBatch(db);
  let touched = 0;

  const markReceivablePaid = (docSnap) => {
    const s = docSnap.data().status;
    if (s !== 'paid' && s !== 'completed') {
      batch.update(docSnap.ref, {
        status: 'paid',
        paid_at: serverTimestamp(),
        payment_order_id: orderId,
        updated_at: serverTimestamp(),
      });
      touched += 1;
    }
  };

  // (a) explicit receivable_ids array
  const explicitIds = Array.isArray(reqData?.receivable_ids) ? reqData.receivable_ids : [];
  // (b) single receivable_id
  if (reqData?.receivable_id && !explicitIds.includes(reqData.receivable_id)) {
    explicitIds.push(reqData.receivable_id);
  }
  for (const rid of explicitIds) {
    try {
      const rDoc = await getDoc(doc(db, 'partner_receivables', rid));
      if (rDoc.exists()) markReceivablePaid(rDoc);
    } catch { /* skip */ }
  }

  // (c) service_request_id 기반
  if (serviceRequestId) {
    const recvSnap = await getDocs(query(
      collection(db, 'partner_receivables'),
      where('service_request_id', '==', serviceRequestId),
    ));
    recvSnap.docs.forEach(markReceivablePaid);
    const commSnap = await getDocs(query(
      collection(db, 'commissions'),
      where('service_request_id', '==', serviceRequestId),
    ));
    commSnap.docs.forEach((d) => {
      if (!d.data().paid_by_partner) {
        batch.update(d.ref, {
          paid_by_partner: true,
          partner_paid_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        });
        touched += 1;
      }
    });
  }

  // (d) payment_request_id로 직접 연결된 receivables (스키마에 따라 존재할 수 있음)
  if (touched === 0) {
    try {
      const linkedSnap = await getDocs(query(
        collection(db, 'partner_receivables'),
        where('payment_request_id', '==', paymentRequestId),
      ));
      linkedSnap.docs.forEach(markReceivablePaid);
    } catch { /* skip */ }
  }

  if (touched > 0) {
    await batch.commit();
  }
};

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('confirming'); // confirming | done | error
  const [message, setMessage] = useState('결제 승인 처리 중...');
  const [kind, setKind] = useState('db_purchase'); // db_purchase | db_purchase_bulk | payment_request | receivable_bulk
  const didRunRef = useRef(false);

  useEffect(() => {
    if (didRunRef.current) return;
    didRunRef.current = true;

    const paymentKey = params.get('paymentKey');
    const orderId = params.get('orderId');
    const amount = Number(params.get('amount'));
    if (!paymentKey || !orderId || !amount) {
      setPhase('error');
      setMessage('필수 결제 정보가 누락됐습니다.');
      return;
    }
    (async () => {
      try {
        await confirmTossPayment({ paymentKey, orderId, amount });
        // 주문 메타 회수 — kind 별 분기
        // localStorage 사용 — 외부 결제앱(카카오페이 등) 호출 후 RN WebView 인스턴스가 갱신되어 sessionStorage 잃어버리는 문제 회피
        const metaRaw = localStorage.getItem(`order:${orderId}`);
        if (metaRaw) {
          try {
            const meta = JSON.parse(metaRaw);
            if (meta?.kind) setKind(meta.kind);
            if (meta?.kind === 'payment_request' && meta?.paymentRequestId) {
              await finalizePaymentRequestPayment({
                paymentRequestId: meta.paymentRequestId,
                orderId,
                paymentKey,
                amount,
                depositorName: meta.depositorName,
                depositorPhone: meta.depositorPhone,
              });
              localStorage.removeItem(`order:${orderId}`);
              setPhase('done');
              setMessage('본사 청구 결제가 완료됐습니다.');
              return;
            }
            if (meta?.kind === 'db_purchase_bulk' && Array.isArray(meta?.srIds) && meta.srIds.length > 0) {
              const prices = meta.prices || {};
              const freeIds = Array.isArray(meta.freeSrIds) ? meta.freeSrIds : [];
              for (const srId of meta.srIds) {
                try {
                  await purchaseDB(srId, meta.partnerUid, { price: Number(prices[srId] || 0), method: '카드(일괄)' });
                } catch (err) { console.error('bulk purchase failed', srId, err); }
              }
              for (const srId of freeIds) {
                try {
                  await purchaseDB(srId, meta.partnerUid, { price: 0, method: '무료(일괄)' });
                } catch (err) { console.error('bulk free purchase failed', srId, err); }
              }
              await addDoc(collection(db, 'payments'), {
                kind: 'db_purchase_bulk',
                payment_order_id: orderId,
                payment_key: paymentKey || null,
                amount,
                partner_id: meta.partnerUid || null,
                service_request_ids: meta.srIds,
                label: 'DB 일괄 구매',
                method: '카드(일괄)',
                status: 'completed',
                paid_at: serverTimestamp(),
                created_at: serverTimestamp(),
              });
              localStorage.removeItem(`order:${orderId}`);
              setPhase('done');
              setMessage(`DB 일괄 구매가 완료됐습니다. (${meta.srIds.length + freeIds.length}건)`);
              return;
            }
            if (meta?.kind === 'receivable_bulk' && Array.isArray(meta?.receivableIds) && meta.receivableIds.length > 0) {
              const batch = writeBatch(db);
              meta.receivableIds.forEach((rid) => {
                batch.update(doc(db, 'partner_receivables', rid), {
                  status: 'paid',
                  paid_at: serverTimestamp(),
                  payment_order_id: orderId,
                  updatedAt: serverTimestamp(),
                });
              });
              await batch.commit();
              // 결제 이력
              await addDoc(collection(db, 'payments'), {
                kind: 'receivable_bulk',
                payment_order_id: orderId,
                payment_key: paymentKey || null,
                amount,
                partner_id: meta.partnerUid || null,
                receivable_ids: meta.receivableIds,
                label: '미수금 일괄 결제',
                status: 'completed',
                paid_at: serverTimestamp(),
                created_at: serverTimestamp(),
              });
              // 결제내역 페이지(partner_payment_requests) 에도 추가 — DB 구매와 일관성 (history 탭에서 노출되도록)
              await addDoc(collection(db, 'partner_payment_requests'), {
                partner_id: meta.partnerUid || null,
                amount,
                method: '카드',
                status: 'completed',
                label: '계약완료 수수료',
                receivable_ids: meta.receivableIds,
                payment_order_id: orderId,
                payment_key: paymentKey || null,
                created_at: serverTimestamp(),
              });
              // 연결 receivables 의 service_request_id 별로:
              //   1) commissions.paid_by_partner = true (제휴업체→본사 결제 완료 표시)
              //   2) service_requests.hq_status = 'settlement_done' (정산완료 → 부동산 측 "완료" 카운트로 잡힘)
              try {
                const commBatch = writeBatch(db);
                const srIdsToFinalize = new Set();
                for (const rid of meta.receivableIds) {
                  const rDoc = await getDoc(doc(db, 'partner_receivables', rid));
                  const srId = rDoc.exists() ? rDoc.data().service_request_id : null;
                  if (!srId) continue;
                  srIdsToFinalize.add(srId);
                  const commSnap = await getDocs(query(
                    collection(db, 'commissions'),
                    where('service_request_id', '==', srId),
                  ));
                  commSnap.docs.forEach((d) => {
                    if (!d.data().paid_by_partner) {
                      commBatch.update(d.ref, {
                        paid_by_partner: true,
                        partner_paid_at: serverTimestamp(),
                        updated_at: serverTimestamp(),
                      });
                    }
                  });
                }
                // service_requests.hq_status 일괄 settlement_done 전환
                for (const srId of srIdsToFinalize) {
                  commBatch.update(doc(db, 'service_requests', srId), {
                    hq_status: 'settlement_done',
                    updated_at: serverTimestamp(),
                  });
                }
                await commBatch.commit();
              } catch (err) { console.error('commission sync failed:', err); }
              localStorage.removeItem(`order:${orderId}`);
              setPhase('done');
              setMessage(`미수금 결제가 완료됐습니다. (${meta.receivableIds.length}건)`);
              return;
            }
            if (meta?.srId && meta?.partnerUid) {
              await purchaseDB(meta.srId, meta.partnerUid, { price: amount, method: '카드' });
              await addDoc(collection(db, 'payments'), {
                kind: 'db_purchase',
                payment_order_id: orderId,
                payment_key: paymentKey || null,
                amount,
                partner_id: meta.partnerUid,
                service_request_id: meta.srId,
                label: 'DB 마켓 구매',
                method: '카드',
                status: 'completed',
                paid_at: serverTimestamp(),
                created_at: serverTimestamp(),
              });
              localStorage.removeItem(`order:${orderId}`);
              setPhase('done');
              setMessage('결제 및 DB 구매가 완료됐습니다.');
              return;
            }
            localStorage.removeItem(`order:${orderId}`);
          } catch (metaErr) {
            // 메타 처리 실패 — silent swallow 하지 말고 화면/콘솔에 노출
            console.error('[PaymentSuccess] meta processing failed:', metaErr);
            setPhase('error');
            setMessage(`결제는 승인됐지만 내역 저장 실패: ${metaErr?.message || metaErr}`);
            return;
          }
        }
        setPhase('done');
        setMessage('결제가 완료됐습니다.');
      } catch (err) {
        console.error('[PaymentSuccess] confirm failed:', err);
        setPhase('error');
        setMessage(err.message || '결제 승인 실패');
      }
    })();
  }, [params]);

  return (
    <Wrap>
      <Card>
        <Title>{phase === 'confirming' ? '결제 처리 중' : phase === 'done' ? '결제 완료' : '결제 실패'}</Title>
        <Desc>{message}</Desc>
        {phase !== 'confirming' && (() => {
          const isSettlement = kind === 'payment_request' || kind === 'receivable_bulk';
          const target = isSettlement ? '/partner/settlement' : '/partner/db?tab=mine';
          const label = isSettlement ? '정산 현황으로 이동' : '내 고객으로 이동';
          return <Btn onClick={() => navigate(target)}>{label}</Btn>;
        })()}
      </Card>
    </Wrap>
  );
};

export default PaymentSuccess;

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F6F8FA;
  padding: 24px;
`;
const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;
`;
const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 12px;
  color: #1a1d29;
`;
const Desc = styled.p`
  font-size: 14px;
  color: #6B7280;
  line-height: 1.6;
  margin: 0 0 24px;
`;
const Btn = styled.button`
  width: 100%;
  padding: 12px;
  background: #4A6CF7;
  color: #fff;
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
`;
